// Shared helpers for the Network query path. Used by SearchBoxTable +
// PasteBoxTable + (transitively) LordOfTheSearch.
//
// PHASE 2 (planned): unchanged when SearchBoxTable + PasteBoxTable
// collapse — both still call buildSubmitUrl. See sibling components
// for fuller phase notes.

import { TissueNetwork } from '../components/shared/sharedTypes';
import { NETWORK_VERSIONS, TISSUE_DB, VERSION_IDENTIFIERS } from '../settings';

// Real production slug shapes:
//   v1 (GIANT) tissue: `kidney`, `cerebrum`, `global` (no suffix);
//     identified by `context.term.database.slug === TISSUE_DB`,
//     plus the `'global'` sentinel.
//   v3 (MAGE) tissue: `kidney-v3`, `cerebrum-v3`, `global-v3`;
//     identified by `context.version.identifier === VERSION_IDENTIFIERS[MAGE]`.
//
// Mirrors filterTissueIntegrations in FMDQueryComponent/fmdMachine.ts
// so SearchBoxTable + PasteBoxTable see the same tissue universe FMD
// does. If FMD ever generalizes its filter, hoist into here and call
// from both places.
export const filterIntegrationsByVersion = (
  integrations: TissueNetwork[],
  version: string,
): TissueNetwork[] => {
  if (!Array.isArray(integrations)) return [];
  if (version === NETWORK_VERSIONS.GIANT) {
    return integrations.filter(integration => {
      if (!integration) return false;
      if (integration.slug === 'global') return true;
      return integration?.context?.term?.database?.slug === TISSUE_DB;
    });
  }
  const versionId = VERSION_IDENTIFIERS[NETWORK_VERSIONS.MAGE];
  return integrations.filter(
    integration =>
      !!integration && integration?.context?.version?.identifier === versionId,
  );
};

// Resolve a URL-supplied tissue slug to its integration object,
// falling back to the version-correct global tissue when the URL
// slug is missing/unknown. The first argument is required to be
// ALREADY-VERSION-FILTERED — the caller (GenePage's tissueIntegrations
// state) does the v1/v3 filtering upstream via filterIntegrationsByVersion
// or its inline equivalent. The named parameter pins the
// responsibility split: re-checking `context.version.identifier`
// inside this resolver would silently drop every v1 tissue
// (blood, cerebrum, ...) — v1 integrations don't reliably carry
// that field — and route every URL to the global fallback.
export const resolveTissueIntegration = (
  versionFilteredIntegrations: TissueNetwork[],
  defaultSlug: string | null | undefined,
  fallbackSlug: string,
): TissueNetwork | null => {
  if (!Array.isArray(versionFilteredIntegrations)) return null;
  if (defaultSlug) {
    const match = versionFilteredIntegrations.find(
      i => i?.slug === defaultSlug,
    );
    if (match) return match;
  }
  return (
    versionFilteredIntegrations.find(i => i?.slug === fallbackSlug) || null
  );
};

// Look up the version of an integration by its slug. The
// integrations list is the SINGLE SOURCE OF TRUTH for which
// version a slug belongs to — version.identifier is set by the
// backend when the data is loaded, so a future v2/v4 lands here
// without code changes (assuming VERSION_IDENTIFIERS is updated).
//
// Used by GenePage on mount to set giantVersion from a URL like
// /gene/3553/blood-v3: dispatch fetchIntegrations (idempotent),
// then call this helper to read the truth from the loaded data.
//
// Returns null when:
//   - slug is missing/empty,
//   - integrations list is empty (caller should wait for data),
//   - slug is not in the integrations list (caller falls back),
//   - the matched integration carries an unknown version
//     identifier (the helper refuses to guess a name for an
//     unmapped version; whoever adds v4 must update VERSION_IDENTIFIERS
//     and they will see a null here as a sentinel).
export const findIntegrationVersion = (
  slug: string | null | undefined,
  integrations: TissueNetwork[],
): string | null => {
  if (!slug) return null;
  if (!Array.isArray(integrations) || integrations.length === 0) return null;
  const match = integrations.find(i => i?.slug === slug);
  if (!match) return null;
  const identifier = match.context?.version?.identifier;
  // v1 (GIANT) integrations don't carry a version field — they're
  // identified by `context.term.database.slug === TISSUE_DB`.
  if (!identifier) {
    if (match.context?.term?.database?.slug === TISSUE_DB) {
      return NETWORK_VERSIONS.GIANT;
    }
    return null;
  }
  // Reverse-lookup version.identifier ('3') → giantVersion key ('v3').
  // VERSION_IDENTIFIERS is { v1: '1', v3: '3' } in settings.
  const entry = Object.entries(VERSION_IDENTIFIERS).find(
    ([, id]) => id === identifier,
  );
  return entry ? entry[0] : null;
};

// Append `&extended_universe=true` (or `?extended_universe=true` if
// the URL has no query string yet) when the flag is on. Used by
// every URL-construction surface that touches the community/module
// path — fmdMachine.fetchCommunity, CommunityPage's
// history.push/replace, CommunityAlerts. Centralizing so one
// caller can't silently drop the user's URL choice.
export const withExtendedUniverse = (
  url: string,
  extendedUniverse: boolean,
): string => {
  if (!extendedUniverse) return url;
  if (url.includes('extended_universe=true')) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}extended_universe=true`;
};

interface SubmitGene {
  entrez: string | number;
}

// Compose the FE URL for submission with pre-submit selectors.
// Returns null when there's nothing extra to encode beyond the legacy
// `/gene/<entrez+joined>` route — caller falls back to its legacy path.
//
// - tissue selected: route to `/gene/<list>/<slug>`. Version is
//   encoded in the slug (e.g. `cerebrum-v3`).
// - no tissue, version=MAGE: encode intent as `?giant_version=v3` on
//   the /gene route. There is no `/network/...` FE route in this
//   repo (see src/routes/index.js); the gene-page result component
//   does not yet honor the query param, but the URL captures the
//   user's MAGE choice for a future enhancement.
// - no tissue, version=GIANT: null (legacy fallback).
export const buildSubmitUrl = (
  tissue: TissueNetwork | null,
  version: string,
  genes: SubmitGene[],
): string | null => {
  if (!Array.isArray(genes) || genes.length === 0) return null;
  const ids = genes.map(g => g.entrez).join('+');
  if (tissue && tissue.slug) {
    return `/gene/${ids}/${tissue.slug}`;
  }
  // Non-default version with no tissue: encode the version so the
  // result page can honor the user's choice. The default GIANT
  // version doesn't need a query param — caller falls back to the
  // legacy /gene/<list> route.
  if (version !== NETWORK_VERSIONS.GIANT) {
    return `/gene/${ids}?giant_version=${version}`;
  }
  return null;
};
